'use client';

import { useState } from 'react';
import { useCurrency } from '@/hooks/useCurrency';
import { SUPPORT_TIERS, SUPPORT_COMPARISON_ROWS } from '@/lib/support-plans';
import { ISupportTier } from '@/interfaces/supportPlan';
import { Language } from '@/lib/translations';
import { getThemeBackgroundClass } from '@/lib/theme';

interface SupportPlansProps {
  lang: Language;
  title?: string;
  subtitle?: string;
  backgroundColor?: 'primary' | 'secondary';
}

export default function SupportPlans({
  lang,
  title,
  subtitle,
  backgroundColor = 'primary',
}: SupportPlansProps) {
  // Annual is the headline offer, so the page opens on it.
  const [isAnnual, setIsAnnual] = useState(true);
  const { formatPrice } = useCurrency();
  const bgClasses = getThemeBackgroundClass(null, backgroundColor);

  const priceLabel = (tier: ISupportTier) => {
    if (tier.pricePerLocation === 'included') {
      return lang === 'en' ? 'Included' : '已包含';
    }
    if (tier.pricePerLocation === 'custom') {
      return lang === 'en' ? 'Custom' : '客製報價';
    }
    const monthly = tier.pricePerLocation;
    const annual = tier.pricePerLocationAnnual;
    if (isAnnual && annual) {
      return formatPrice(annual.usd, annual.twd);
    }
    return formatPrice(monthly.usd, monthly.twd);
  };

  const priceCadence = (tier: ISupportTier) => {
    if (tier.pricePerLocation === 'included') {
      return lang === 'en'
        ? 'with every Omcean Booking plan'
        : '所有 Omcean Booking 方案皆包含';
    }
    if (tier.pricePerLocation === 'custom') {
      return lang === 'en'
        ? "Let's design the right fit"
        : '為您量身打造合適方案';
    }
    if (isAnnual) {
      return lang === 'en' ? 'per location · billed annually' : '每分店・年繳';
    }
    return lang === 'en' ? 'per location · per month' : '每分店・每月';
  };

  return (
    <section className={`${bgClasses} py-20`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(title || subtitle) && (
          <div className="text-center mb-12 max-w-3xl mx-auto">
            {title && (
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
            )}
            {subtitle && (
              <p
                className="text-lg"
                style={{ color: 'var(--color-foreground-secondary)' }}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Toggles row */}
        <div className="flex flex-wrap justify-center items-center gap-4 mb-12">
          {/* Monthly / Annual */}
          <div
            className="rounded-full p-1 flex items-center shadow-sm"
            style={{
              backgroundColor: 'var(--color-card-background)',
              border: '1px solid var(--color-card-border)',
            }}
          >
            <button
              type="button"
              aria-pressed={!isAnnual}
              onClick={() => setIsAnnual(false)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                !isAnnual
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              style={
                !isAnnual ? {} : { color: 'var(--color-foreground-secondary)' }
              }
            >
              {lang === 'en' ? 'Monthly' : '月付'}
            </button>
            <button
              type="button"
              aria-pressed={isAnnual}
              onClick={() => setIsAnnual(true)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors relative ${
                isAnnual
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              style={
                isAnnual ? {} : { color: 'var(--color-foreground-secondary)' }
              }
            >
              {lang === 'en' ? 'Annual' : '年付'}
              <span className="absolute -top-2 -right-1 bg-green-700 text-white text-xs px-1.5 py-0.5 rounded-full">
                {lang === 'en' ? '2 months free' : '省 2 個月'}
              </span>
            </button>
          </div>
        </div>

        <ComparisonView
          lang={lang}
          priceLabel={priceLabel}
          priceCadence={priceCadence}
        />

        {/* Footnote */}
        <p
          className="text-center text-sm mt-12 max-w-2xl mx-auto"
          style={{ color: 'var(--color-foreground-secondary)' }}
        >
          {lang === 'en'
            ? 'All tiers cover both English and Traditional Chinese (繁體中文). Priority and Enterprise are billed per location so you only pay for the branches you support.'
            : '所有支援方案均涵蓋英文與繁體中文。優先與企業支援依分店數計費，僅針對您需要支援的分店收費。'}
        </p>
      </div>
    </section>
  );
}

interface ViewProps {
  lang: Language;
  priceLabel: (tier: ISupportTier) => string;
  priceCadence: (tier: ISupportTier) => string;
}

function ComparisonView({ lang, priceLabel, priceCadence }: ViewProps) {
  return (
    <>
      <p
        className="mb-3 flex items-center justify-end gap-2 text-sm lg:hidden"
        style={{ color: 'var(--color-foreground-secondary)' }}
      >
        {lang === 'en'
          ? 'Swipe to compare support plans'
          : '左右滑動比較支援方案'}
        <span aria-hidden="true">↔</span>
      </p>
      <div
        role="region"
        aria-label={lang === 'en' ? 'Compare support plans' : '支援方案比較'}
        tabIndex={0}
        className="overflow-x-auto overscroll-x-contain rounded-2xl shadow-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#3880ff]"
      >
        <div
          className="min-w-[880px] overflow-hidden rounded-2xl"
          style={{
            backgroundColor: 'var(--color-card-background)',
            border: '1px solid var(--color-card-border)',
          }}
        >
          {/* Header row */}
          <div className="grid grid-cols-4 border-b border-gray-200 dark:border-gray-700 items-stretch">
            {/* Compare support label */}
            <div className="p-6 flex items-center gap-2">
              <svg
                className="w-5 h-5 shrink-0 text-[var(--link-text)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.415m-1.414-1.415L3 3m8.293 8.293l1.414 1.414"
                />
              </svg>
              <span className="text-[var(--link-text)] font-semibold">
                {lang === 'en' ? 'Compare support' : '支援方案比較'}
              </span>
            </div>

            {/* Tier header cells — uniform height via flex */}
            {SUPPORT_TIERS.map(tier => (
              <div
                key={tier.id}
                className="p-6 text-center relative flex flex-col"
                style={{
                  backgroundColor: tier.highlight
                    ? 'rgba(56, 128, 255, 0.05)'
                    : undefined,
                  borderTop: tier.highlight ? '3px solid #3880ff' : undefined,
                }}
              >
                {/* Top: MOST POPULAR badge slot (fixed height to align names) */}
                <div className="h-6 mb-1">
                  {tier.highlight && (
                    <div className="text-[var(--link-text)] text-xs font-bold flex items-center justify-center gap-1">
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {lang === 'en' ? 'MOST POPULAR' : '最熱門選擇'}
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold mb-2">{tier.name[lang]}</h3>
                <div className="text-3xl font-bold mb-1">
                  {priceLabel(tier)}
                </div>
                <div
                  className="text-xs"
                  style={{ color: 'var(--color-foreground-secondary)' }}
                >
                  {priceCadence(tier)}
                </div>

                {/* Status pill — pinned to bottom for uniform alignment */}
                <div className="mt-auto pt-5 flex justify-center">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                      tier.highlight
                        ? 'bg-[#3880ff]/10 text-[var(--link-text)]'
                        : tier.id === 'enterprise'
                          ? 'bg-gray-900/5 dark:bg-gray-100/10'
                          : 'bg-green-500/10 text-green-700 dark:text-green-400'
                    }`}
                    style={
                      !tier.highlight && tier.id === 'enterprise'
                        ? { color: 'var(--color-foreground)' }
                        : undefined
                    }
                  >
                    {tier.id === 'standard' && (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={3}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    {tier.cta[lang]}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Feature rows */}
          {SUPPORT_COMPARISON_ROWS.map((row, idx) => (
            <div
              key={idx}
              className="grid grid-cols-4 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
              style={{
                backgroundColor:
                  idx % 2 === 1
                    ? 'var(--color-background-subtle, rgba(0,0,0,0.02))'
                    : undefined,
              }}
            >
              <div className="p-4 text-sm font-medium flex items-center">
                {row.label[lang]}
              </div>
              <ComparisonCell
                value={row.values.standard}
                lang={lang}
                highlight={false}
              />
              <ComparisonCell
                value={row.values.priority}
                lang={lang}
                highlight={true}
              />
              <ComparisonCell
                value={row.values.enterprise}
                lang={lang}
                highlight={false}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ComparisonCell({
  value,
  lang,
  highlight,
}: {
  value: boolean | { en: string; zh: string };
  lang: Language;
  highlight: boolean;
}) {
  return (
    <div
      className="p-4 flex items-center justify-center text-center text-sm"
      style={{
        backgroundColor: highlight ? 'rgba(56, 128, 255, 0.04)' : undefined,
      }}
    >
      {typeof value === 'boolean' ? (
        value ? (
          <CheckIcon />
        ) : (
          <span
            className="text-2xl"
            style={{ color: 'var(--color-foreground-secondary)' }}
          >
            —
          </span>
        )
      ) : (
        <span className="font-medium">{value[lang]}</span>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      className="w-6 h-6 text-green-500 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
